import instanceJWT from "./apiJwt";

import type { GpNegotiationConfig, GpPluginRank } from "../models/eplex/GroupPricing";

// ── Domain types reflecting live API ──────────────────────────────────────

export type ZakState = 1 | 2 | 3 | 4 | 5 | 6 | 7;

export interface ZakPurchaseDto {
  uuid: string;
  profile_id: number;
  client_id: number;
  title: string;
  doc_osn_uuid: string | null;
  doc_osn_title: string | null;
  state: ZakState;
  is_archive: 0 | 1;
  is_autoorder: 0 | 1;
  plugins: GpPluginRank[];
  brands_trusted: string;
  purchase_deadline: string | null;
  extra_days: number;
  order_type: number;
  actualization_period: number;
  price_diff_percent: number;
  items_filter_rules: Record<string, unknown> | null;
  algos: number[] | null;
  add_info: Record<string, unknown> | null;
  trigger_settings: Record<string, unknown> | null;
  comment: string | null;
  created_at: string;
  updated_at: string;
  negotiation_state: number | null;
  base_recommendation_uuid: string | null;
  gs_request_id?: string | null;
  gs_data?: {
    status?: string;
    estimate_time?: string;
    status_hint?: string;
    info?: { total: number; in_progress: number; finished: number };
  };
}

export interface ZakPurchaseItemDto {
  uuid: string;
  purchase_uuid: string;
  profile_id: number;
  client_id: number;
  idx: number;
  article: string;
  brand: string;
  name: string | null;
  price: number | string;
  qty: number;
  qty_planned?: number | null;
  qty_ready?: number | null;
  qty_ordered?: number | null;
  is_no_replace: 0 | 1;
  is_important: 0 | 1;
  is_autoorder: 0 | 1;
  brands_trusted: string | null;
  comment: string | null;
  add_info: Record<string, unknown> | null;
  sreqid: string | null;
  last_search_at: string | null;
  recommendation_summary?: Record<string, { max_time: number | null; sum: number; sum_qty: number; state: number }>;
}

export interface ZakRecommendationDto {
  uuid: string;
  profile_id: number;
  client_id: number;
  purchase_uuid: string;
  /** Номер алгоритма 1..8; `null` — пользовательская (кастомная) рекомендация. */
  algo: number | null;
  title: string;
  state: number;
  sum: number;
  time_max: number;
  qty_planned_sum: number;
  qty_ready_sum: number;
  qty_ordered_sum: number;
  purchase_qty_sum: number;
  created_at: string;
  updated_at: string;
}

export interface ZakRecommendationItemDto {
  uuid: string;
  recommendation_uuid: string;
  purchase_item_uuid: string;
  is_pinned: 0 | 1;
  sreqid: string | null;
  plugin_id: number | null;
  plugin_title: string | null;
  pos_idx: number | null;
  last_search_at: string | null;
  brand: string | null;
  brand_id: number | null;
  article: string | null;
  name: string | null;
  price: number | string | null;
  count: number | string | null;
  time_int: number | null;
  qty_planned: number | null;
  qty_ready: number | null;
  qty_ordered: number | null;
  mo_request_id: string | null;
  mo_state: number | null;
  zakaz_num_text?: string | null;
  screen_id?: number | null;
  order_id?: number | null;
  date_order_planned?: string | null;
  date_order_fact?: string | null;
  add_info?: Record<string, unknown> | null;
}

export interface ZakNegotiationDto {
  uuid: string;
  code: string;
  purchase_uuid: string;
  recommendation_uuids: string[];
  selected_recommendation_uuid: string | null;
  tm_percent: number;
  delivery_type: number;
  phone: string | null;
  email: string | null;
  manager_name: string | null;
  manager_phone: string | null;
  manager_email: string | null;
  manager_comment: string | null;
  add_info: Record<string, unknown> | null;
  info_type: number;
  is_autostart: 0 | 1;
  is_allowed_orders: 0 | 1;
  ttl_days: number;
  client_comment: string | null;
  prepay: number | null;
  created_at: string;
  state?: number;
  url?: string;
}

export interface ZakAlgoDescription {
  [algo: string]: string;
}

export interface ZakPaginatedResponse<T> {
  data: T[];
  meta: {
    order_by: string;
    order_dir: "ASC" | "DESC";
    limit: number;
    offset: number;
    total_count: number;
  };
}

// API responses come wrapped: { status, error, data: { data: ..., meta: ... } }
interface ApiEnvelope<T> {
  status: boolean;
  error: boolean | Record<string, unknown>;
  data: T;
}

function unwrap<T>(env: ApiEnvelope<T>): T {
  if (env.error && typeof env.error === "object") {
    const err = env.error as { message?: string; code?: number };
    throw new Error(err.message ?? "API error");
  }
  return env.data;
}

const BASE = "eplex/zakooper";

/**
 * Некоторые списковые эндпоинты возвращают `ZakPaginatedResponse` с реальным
 * `meta.total_count`, но вызывающий код исторически не передавал limit/offset
 * и не проверял `total_count` — при наборе больше одной backend-страницы
 * (позиции большой закупки, items большой рекомендации) молча получали только
 * первую страницу: часть позиций выглядела «без рекомендаций» или с
 * недосчитанным количеством, хотя предложений было достаточно (CAB-626).
 *
 * Первый вызов — как раньше, без limit/offset (backend применяет свой
 * дефолт и сообщает его в meta.limit). Дальше догружаем оставшиеся страницы
 * тем же limit, пока не наберём meta.total_count. Кап в 50 страниц — страховка
 * от аномального backend-ответа (limit=0 или растущий total_count), а не
 * ожидаемый сценарий.
 */
async function fetchAllPages<T>(
  fetchPage: (page?: { limit: number; offset: number }) => Promise<ZakPaginatedResponse<T>>,
): Promise<ZakPaginatedResponse<T>> {
  const first = await fetchPage();
  const { limit, total_count } = first.meta;
  if (limit <= 0 || first.data.length >= total_count) return first;

  const data = [...first.data];
  for (let page = 0; page < 50 && data.length < total_count; page++) {
    const next = await fetchPage({ limit, offset: data.length });
    if (next.data.length === 0) break;
    data.push(...next.data);
  }
  return { data, meta: { ...first.meta, offset: 0, total_count } };
}

// ── Purchases ─────────────────────────────────────────────────────────────

export async function listPurchases(params: {
  limit?: number;
  offset?: number;
  order_by?: string;
  order_dir?: "ASC" | "DESC";
  is_archive?: 0 | 1;
}): Promise<ZakPaginatedResponse<ZakPurchaseDto>> {
  const response = await instanceJWT.get(`${BASE}/purchases`, { params });
  return unwrap(response.data as ApiEnvelope<ZakPaginatedResponse<ZakPurchaseDto>>);
}

export async function getPurchase(uuid: string, options?: { silent?: boolean }): Promise<ZakPurchaseDto> {
  const response = await instanceJWT.get(`${BASE}/purchases/${uuid}`, {
    _silent: options?.silent,
  } as never);
  const env = response.data as ApiEnvelope<{ data: ZakPurchaseDto; error_code: number; status: string }>;
  return unwrap(env).data;
}

export interface CreatePurchasePayload {
  title: string;
  doc_osn_uuid?: string | null;
  doc_osn_title?: string | null;
  is_autoorder?: 0 | 1;
  plugins?: GpPluginRank[];
  brands_trusted?: string;
  purchase_deadline?: string | null;
  extra_days?: number;
  order_type?: number;
  actualization_period?: number;
  price_diff_percent?: number;
  items_filter_rules?: Record<string, unknown> | null;
  algos?: number[] | null;
  add_info?: Record<string, unknown> | null;
  comment?: string | null;
  trigger_settings?: Record<string, unknown> | null;
}

export async function createPurchase(payload: CreatePurchasePayload): Promise<ZakPurchaseDto> {
  const response = await instanceJWT.post(`${BASE}/purchases`, { item: payload });
  const env = response.data as ApiEnvelope<{ data: ZakPurchaseDto; error_code: number; status: string }>;
  return unwrap(env).data;
}

export async function updatePurchase(uuid: string, payload: Partial<CreatePurchasePayload>): Promise<ZakPurchaseDto> {
  const response = await instanceJWT.put(`${BASE}/purchases/${uuid}`, { item: payload });
  const env = response.data as ApiEnvelope<{ data: ZakPurchaseDto; error_code: number; status: string }>;
  return unwrap(env).data;
}

export async function deletePurchase(uuid: string): Promise<void> {
  await instanceJWT.delete(`${BASE}/purchases/${uuid}`);
}

export async function startPurchase(uuid: string): Promise<void> {
  await instanceJWT.get(`${BASE}/purchases/${uuid}/start`);
}

// `stop` и `archivate` — это GET-эндпоинты по Postman'у. Предыдущее
// POST-ошибочно отдавало 407 «Неверные параметры запроса». Бэк трактует
// их как side-effect-команды без тела.
export async function stopPurchase(uuid: string): Promise<void> {
  await instanceJWT.get(`${BASE}/purchases/${uuid}/stop`);
}

export async function archivatePurchase(uuid: string): Promise<void> {
  await instanceJWT.get(`${BASE}/purchases/${uuid}/archivate`);
}

// ── Purchase items ────────────────────────────────────────────────────────

export async function listPurchaseItems(
  uuid: string,
  withSummary = false,
): Promise<ZakPaginatedResponse<ZakPurchaseItemDto>> {
  return fetchAllPages((page) => {
    const params: Record<string, string | number> = {};
    if (withSummary) params.with_recommendation_items_summary = "true";
    if (page) {
      params.limit = page.limit;
      params.offset = page.offset;
    }
    return instanceJWT
      .get(`${BASE}/purchases/${uuid}/items`, { params })
      .then((response) => unwrap(response.data as ApiEnvelope<ZakPaginatedResponse<ZakPurchaseItemDto>>));
  });
}

export interface PurchaseItemPayload {
  article: string;
  brand: string;
  name?: string;
  price?: number;
  qty: number;
  is_no_replace?: 0 | 1;
  is_important?: 0 | 1;
  is_autoorder?: 0 | 1;
  brands_trusted?: string | null;
  comment?: string | null;
}

export async function addPurchaseItems(uuid: string, items: PurchaseItemPayload[]): Promise<void> {
  await instanceJWT.post(`${BASE}/purchases/${uuid}/items`, { items });
}

export async function updatePurchaseItem(itemUuid: string, payload: Partial<PurchaseItemPayload>): Promise<void> {
  await instanceJWT.put(`${BASE}/purchase_items/${itemUuid}`, { item: payload });
}

export async function deletePurchaseItem(itemUuid: string): Promise<void> {
  await instanceJWT.delete(`${BASE}/purchase_items/${itemUuid}`);
}

// ── Recommendations ───────────────────────────────────────────────────────

export async function listRecommendations(purchaseUuid: string): Promise<ZakPaginatedResponse<ZakRecommendationDto>> {
  const response = await instanceJWT.get(`${BASE}/purchases/${purchaseUuid}/recommendations`);
  return unwrap(response.data as ApiEnvelope<ZakPaginatedResponse<ZakRecommendationDto>>);
}

export async function getRecommendation(uuid: string): Promise<ZakRecommendationDto> {
  const response = await instanceJWT.get(`${BASE}/recommendations/${uuid}`);
  const env = response.data as ApiEnvelope<{ data: ZakRecommendationDto }>;
  return unwrap(env).data;
}

export async function listRecommendationItems(
  recUuid: string,
): Promise<ZakPaginatedResponse<ZakRecommendationItemDto>> {
  return fetchAllPages((page) => {
    const params = page ? { limit: page.limit, offset: page.offset } : undefined;
    return instanceJWT
      .get(`${BASE}/recommendations/${recUuid}/items`, { params })
      .then((response) => unwrap(response.data as ApiEnvelope<ZakPaginatedResponse<ZakRecommendationItemDto>>));
  });
}

export async function copyRecommendation(uuid: string, title?: string): Promise<ZakRecommendationDto> {
  const payload: Record<string, string> = {};
  if (title) payload.title = title;
  const response = await instanceJWT.post(`${BASE}/recommendations/${uuid}/copy`, payload);
  const env = response.data as ApiEnvelope<{ data: ZakRecommendationDto }>;
  return unwrap(env).data;
}

export async function startRecommendation(uuid: string): Promise<void> {
  await instanceJWT.get(`${BASE}/recommendations/${uuid}/start`);
}

export async function getAlgoDescriptions(): Promise<ZakAlgoDescription> {
  const response = await instanceJWT.get(`${BASE}/recommendations/description`);
  const env = response.data as ApiEnvelope<{ data: ZakAlgoDescription }>;
  return unwrap(env).data;
}

// ── Negotiation ───────────────────────────────────────────────────────────

export async function getNegotiation(purchaseUuid: string): Promise<ZakNegotiationDto | null> {
  try {
    const response = await instanceJWT.get(`${BASE}/purchases/${purchaseUuid}/negotiate`);
    const env = response.data as ApiEnvelope<{ data: ZakNegotiationDto | unknown[] }>;
    const inner = unwrap(env).data;
    // Бэкенд возвращает пустой массив [], когда согласования ещё нет.
    if (Array.isArray(inner)) return null;
    if (!inner || typeof inner !== "object") return null;
    return inner;
  } catch {
    return null;
  }
}

export async function createNegotiation(
  purchaseUuid: string,
  item: Partial<GpNegotiationConfig> & {
    recommendation_uuids: string[];
    selected_recommendation_uuid?: string;
  },
): Promise<ZakNegotiationDto> {
  // Backend требует обёртку { item: ... } (см. Postman: Start purchase to Negotiate).
  const response = await instanceJWT.post(`${BASE}/purchases/${purchaseUuid}/negotiate`, { item });
  const env = response.data as ApiEnvelope<{ data: ZakNegotiationDto }>;
  return unwrap(env).data;
}

export async function updateNegotiation(
  purchaseUuid: string,
  item: Partial<GpNegotiationConfig> & { recommendation_uuids?: string[] },
): Promise<ZakNegotiationDto> {
  const response = await instanceJWT.put(`${BASE}/purchases/${purchaseUuid}/negotiate`, { item });
  const env = response.data as ApiEnvelope<{ data: ZakNegotiationDto }>;
  return unwrap(env).data;
}

export async function sendNegotiation(
  purchaseUuid: string,
  item: Partial<GpNegotiationConfig> & { recommendation_uuids?: string[] },
): Promise<void> {
  await instanceJWT.post(`${BASE}/purchases/${purchaseUuid}/negotiate/send`, { item });
}

export async function deleteNegotiation(purchaseUuid: string): Promise<void> {
  await instanceJWT.delete(`${BASE}/purchases/${purchaseUuid}/negotiate`);
}

// ── Recommendation items operations ───────────────────────────────────────
//
// Полный flow редактирования рекомендации (восстановлен из Postman-коллекции
// "Eplex api" → Zakooper, май 2026):
//
// 1. Системные рекомендации (algo 1..8) генерируются автоматически после старта
//    проценки (`GET /purchases/<uuid>/start`). Их состав менять нельзя.
// 2. Чтобы получить редактируемую рекомендацию, есть три пути:
//      (а) `POST /purchases/<uuid>/recommendations {}` — создать пустую кастомную
//          (algo=null), затем `POST /recommendation_items/copy` или
//          `POST /purchase_items/<piuuid>/recommendations/<recuuid>/items`
//          добавить туда нужные офферы.
//      (б) `POST /recommendations/<uuid>/copy` — скопировать всю системную
//          рекомендацию целиком в свою (заголовок и состав).
//      (в) Постепенное копирование `POST /recommendation_items/<uuid>/copy` /
//          `POST /recommendation_items/copy` офферов из системной рек в свою.
// 3. На кастомной рек можно: `PUT /recommendation_items/<uuid>` менять
//    qty_planned, `DELETE /recommendation_items/<uuid>` удалять,
//    `POST /recommendation_items/<uuid>/replace` или batch `/replace` —
//    заменить оффер на другой по той же purchase_item.
// 4. `GET /recommendations/<uuid>/start` — назначает рекомендацию основной
//    и стартует процесс заказа: система раскидывает все её items по очередям MO.
// 5. `POST /recommendation_items/make_order {items: [uuid…]}` — после старта
//    рекомендации добавляет конкретные items в очередь Make Order вручную
//    (бэк сам решает по правилам, какие плагины допустимы).

/**
 * Создать пустую кастомную рекомендацию (algo=null). Бэк допускает ОДНУ
 * кастомную рекомендацию на закупку — повторный вызов вернёт 500 "Уже
 * существует". Используй getOrCreateCustomRecommendation для безопасной
 * идемпотентности.
 */
export async function createBlankRecommendation(purchaseUuid: string): Promise<ZakRecommendationDto> {
  const response = await instanceJWT.post(`${BASE}/purchases/${purchaseUuid}/recommendations`, {});
  const env = response.data as ApiEnvelope<{ data: ZakRecommendationDto }>;
  return unwrap(env).data;
}

/**
 * Идемпотентно: найдёт существующую кастомную рекомендацию (algo=null) для
 * закупки, либо создаст пустую.
 */
export async function getOrCreateCustomRecommendation(purchaseUuid: string): Promise<ZakRecommendationDto> {
  const all = await listRecommendations(purchaseUuid);
  const existing = all.data.find((r) => r.algo === null);
  if (existing) return existing;
  return createBlankRecommendation(purchaseUuid);
}

/**
 * Скопировать ОДИН оффер из системной рекомендации в кастомную.
 * Source — это recommendation_item.uuid из системной рек.
 * Target — uuid кастомной рек, в которую копируем.
 */
export async function copyRecommendationItem(
  sourceItemUuid: string,
  targetRecommendationUuid: string,
): Promise<ZakRecommendationItemDto> {
  const response = await instanceJWT.post(`${BASE}/recommendation_items/${sourceItemUuid}/copy`, {
    recommendation_uuid: targetRecommendationUuid,
  });
  const env = response.data as ApiEnvelope<{ data: ZakRecommendationItemDto }>;
  return unwrap(env).data;
}

/**
 * Скопировать ВСЕ офферы одной позиции (purchase_item) из системной
 * рекомендации в кастомную пользователя. recommendation_uuid в payload —
 * это SOURCE (системная рек). Цель — автоматически кастомная пользователя
 * для той же закупки.
 */
export async function copyAllOffersFromSystemRec(payload: {
  recommendation_uuid: string;
  purchase_item_uuid: string;
}): Promise<void> {
  await instanceJWT.post(`${BASE}/recommendation_items/copy`, payload);
}

export async function addRecommendationItemByTriplet(
  purchaseItemUuid: string,
  recommendationUuid: string,
  items: Array<{ sreqid: string; plugin_id: number | string; pos_idx: number; qty_planned: number }>,
): Promise<void> {
  await instanceJWT.post(`${BASE}/purchase_items/${purchaseItemUuid}/recommendations/${recommendationUuid}/items`, {
    items,
  });
}

export async function updateRecommendationItem(
  riUuid: string,
  patch: Partial<{ qty_planned: number; is_pinned: 0 | 1 }>,
): Promise<void> {
  await instanceJWT.put(`${BASE}/recommendation_items/${riUuid}`, { item: patch });
}

export async function deleteRecommendationItem(riUuid: string): Promise<void> {
  await instanceJWT.delete(`${BASE}/recommendation_items/${riUuid}`);
}

export async function listRecommendationItemsByPurchaseItem(
  purchaseItemUuid: string,
): Promise<ZakPaginatedResponse<ZakRecommendationItemDto>> {
  const response = await instanceJWT.get(`${BASE}/purchase_items/${purchaseItemUuid}/recommendation_items`);
  return unwrap(response.data as ApiEnvelope<ZakPaginatedResponse<ZakRecommendationItemDto>>);
}

/**
 * Плоский список рекомендательных позиций клиента — без привязки к подбору.
 * Нужен, чтобы по `uuid` позиции (его бэк кладёт в `add_info.e0RecommendationItemUUID`
 * строки корзины при оформлении из КП) найти её рекомендацию и через неё — подбор.
 *
 * Сортировка по `last_search_at DESC` даёт свежие позиции первыми: журнал показывает
 * недавние заказы, поэтому одного запроса с запасом хватает, и обход не зависит от
 * всей истории клиента. Проверено на стенде: `limit`/`order_by`/`order_dir` бэк учитывает.
 */
export async function listRecentRecommendationItems(
  limit: number,
): Promise<ZakPaginatedResponse<ZakRecommendationItemDto>> {
  const response = await instanceJWT.get(`${BASE}/recommendation_items`, {
    params: { limit, order_by: "last_search_at", order_dir: "DESC" },
    _silent: true,
  } as never);
  return unwrap(response.data as ApiEnvelope<ZakPaginatedResponse<ZakRecommendationItemDto>>);
}

/**
 * Один офер из результатов поиска (для замены/добавления в рекомендацию).
 * sreqid берётся из contextual recommendation_item (см. SearchResultsResponse.recommendation_items[0]).
 */
export interface ZakSearchResultOffer {
  brand: string;
  brand_id: string | number;
  article: string;
  name: string;
  plugin_id: string | number;
  pos_idx: number;
  cost: string | number;
  count: number | string;
  time_int: number;
  time_orig?: number;
  city?: string;
  chance?: number;
  stock?: number;
  markers?: Record<string, unknown>;
}

export interface ZakSearchResultsResponse {
  /** Текущий offer (тот, с которого вызывали /search_results) — содержит sreqid. */
  recommendation_items: ZakRecommendationItemDto[];
  /** Все альтернативные офферы по этому артикулу (десятки/тысячи). */
  results: ZakSearchResultOffer[];
}

/**
 * Получить альтернативные офферы для конкретного recommendation_item.
 * Возвращает текущий ri (с sreqid) + список альтернативных предложений
 * из поиска. sreqid общий для всего списка — для добавления выбранной
 * альтернативы в рек используй addRecommendationItemByTriplet с sreqid
 * из recommendation_items[0].sreqid.
 */
export async function getSearchResultsForRecommendationItem(
  riUuid: string,
  params?: { limit?: number; offset?: number },
): Promise<ZakSearchResultsResponse> {
  const qs = new URLSearchParams();
  if (params?.limit !== undefined) qs.set("limit", String(params.limit));
  if (params?.offset !== undefined) qs.set("offset", String(params.offset));
  const query = qs.toString() ? `?${qs.toString()}` : "";
  // _silent: во время проценки бэк штатно отвечает 500/407 «результаты не найдены» —
  // не пугаем пользователя тостом (результаты появятся позже).
  const response = await instanceJWT.get(`${BASE}/recommendation_items/${riUuid}/search_results${query}`, {
    _silent: true,
  } as never);
  return unwrap(response.data as ApiEnvelope<ZakSearchResultsResponse>);
}

/**
 * То же самое, но по purchase_item — для случая, когда у нас нет ri в текущей
 * рекомендации (например, добавляем новую позицию из поиска в кастомную).
 */
export async function getSearchResultsForPurchaseItem(
  purchaseItemUuid: string,
  params?: { limit?: number; offset?: number },
): Promise<ZakSearchResultsResponse> {
  const qs = new URLSearchParams();
  if (params?.limit !== undefined) qs.set("limit", String(params.limit));
  if (params?.offset !== undefined) qs.set("offset", String(params.offset));
  const query = qs.toString() ? `?${qs.toString()}` : "";
  // _silent: во время проценки бэк штатно отвечает 500/407 «результаты не найдены».
  const response = await instanceJWT.get(`${BASE}/purchase_items/${purchaseItemUuid}/search_results${query}`, {
    _silent: true,
  } as never);
  return unwrap(response.data as ApiEnvelope<ZakSearchResultsResponse>);
}

export async function makeOrderForRecommendationItems(items: string[]): Promise<void> {
  await instanceJWT.post(`${BASE}/recommendation_items/make_order`, { items });
}
